<template>
  <div data-view="business-users">
    <router-view />

    <layout-logged-frame
      :title="$t('business_members')"
      :total-count="pagination.totalItems"
    >
      <template v-slot:header-actions>
        <v-btn
          color="primary"
          :disabled="!isBusinessAdmin"
          @click="$router.push({ name: 'business-users-invite' })"
        >
          <v-icon>add</v-icon>
          {{ $t('invite_member') }}
        </v-btn>
      </template>

      <template v-slot:content>
        <BusinessMembersFilter
          :is-skeleton-loading="isSkeletonLoading"
          ref="dealsfilter"
          @filter="onFilter"
          @clearFilters="onClearFilters"
        />

        <table-base
          :columns="membersColumns"
          :items="businessMembers"
          :pages="totalPages"
          :pagination="pagination"
          :is-loading="isLoadingMembers"
          has-custom-items-template
          :can-edit-columns="false"
          :on-update-pagination="onUpdatePagination"
          :on-change-page="onChangePage"
          :no-data-title="$t('no_business_members')"
          :no-data-subtitle="$t('start_to_use_our_products')"
        >
          <template v-slot:item-cell="scope">
            <template v-if="scope.cell.column.value === 'name'">
              <span class="d-flex align-center ga-2">
                {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
                <v-chip
                  v-if="scope.cell.item.account_status.id === 0"
                  variant="outlined"
                  color="error"
                  size="small"
                >
                  Blocked account
                </v-chip>
              </span>
            </template>

            <template v-else-if="scope.cell.column.value === 'status.name'">
              <!-- <div class="d-flex justify-end"> -->
              <StatusSelect
                :item="scope.cell.item"
                :update-items="getMembers"
              />
              <!-- </div> -->
            </template>

            <template v-else-if="scope.cell.column.value === 'role.name'">
              <!-- <div class="d-flex justify-end"> -->
              <RoleSelect
                :item="scope.cell.item"
                :update-items="getMembers"
              />
              <!-- </div> -->
            </template>

            <template v-else>
              {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
            </template>
          </template>

          <template v-slot:no-data-action>
            <v-btn
              color="primary"
              :disabled="!isBusinessAdmin"
              @click="$router.push({ name: 'business-users-invite' })"
            >
              <v-icon>add</v-icon>
              {{ $t('invite_member') }}
            </v-btn>
          </template>
        </table-base>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import BusinessMembersFilter from '../../components/forms/filters/BusinessMembers';
  import StatusSelect from '../../components/panels/businessUsers/StatusSelect';
  import RoleSelect from '../../components/panels/businessUsers/RoleSelect';
  import { isStr, extractNestedProp } from '../../utils';
  import membersColumns from '../../config/businessUsers/columns';

  export default {
    components: {
      BusinessMembersFilter,
      StatusSelect,
      RoleSelect,
    },
    data() {
      return {
        membersColumns,
        pagination: {
          order: 'desc' ,
          page: 1,
          rowsPerPage: 15,
          sortBy: 'created_at',
          totalItems: 0,
          search: '',
        },
        query: {},
        hasLoadedMembers: false,
        totalPages: 0,
        isLoadingMembers: false,
        isSkeletonLoading: true,
      };
    },
    computed: {
      activeUser() {
        return this.$store.getters['auth/user'];
      },
      businessMembers() {
        return this.$store.getters['businessUsers/items'];
      },
      isBusinessAdmin() {
        return this.activeUser.is_business_admin;
      },
      canInviteUser() {
        return this.$ability.can('invite', 'user');
      },
      currentRoute() {
        return this.$route;
      },
    },
    watch: {
      currentRoute(to, from) {
        if (to.query?.status === 'success' && from.name === 'business-users-invite' && to.name === 'business-users') {
          this.getMembers();
        }
      },
    },
    created() {
      this.getStatuses();
      this.getSubroles();
    },
    methods: {
      onClearFilters() {
        this.query.page = 1;
      },
      getMembers({
        page,
        order,
        sort,
      } = {}) {
        const query = {
          ...this.query,
          page: page ?? this.pagination.page,
          order: order ?? this.pagination.order,
          sort: sort ?? this.pagination.sortBy,
        };

        this.isLoadingMembers = true;
        this.pagination.order = order;

        return this.$store
          .dispatch('businessUsers/getItems', query)
          .then((res) => {
            const { itemsMeta } = res;
            const { total, current_page, last_page } = itemsMeta;

            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;
            this.isLoadingMembers = false;
            this.isSkeletonLoading = false;

            return res;
          })
          .catch((err) => {
            this.isLoadingMembers = false;
            this.isSkeletonLoading = false;
            return err;
          });
      },
      getSubroles() {
        return this.$store.dispatch('users/getSubRoles');
      },
      getStatuses() {
        return this.$store.dispatch('businessUsers/getStatuses');
      },
      onFilter(filters) {
        this.query = this.getSearchQuery(filters);
        this.query.page = 1;

        this.getMembers();
      },
      getSearchQuery({ search, dates, status, role }) {
        const query = {};

        query.role = role;
        query.search = search;
        query.date_filter = dates;
        query.status = status;
        query.page = this.query.page;

        return query;
      },
      onUpdatePagination({ page, order, sortBy }) {
        const { query, pagination } = this;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

        const nextQuery = {
          ...query,
          page,
          order,
          sort: isStr(sortBy) ? sortBy : pagination.sortBy,
        };

        this.query = nextQuery;

        this.getMembers(nextQuery);
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          order,
          sort: sortBy,
        };

        this.query = nextQuery;

        this.getMembers(nextQuery);
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
    },
  };
</script>
